/**
 * Browser cookie helpers for persisting the active language.
 *
 * The cookie *name* is owned by the consuming app (it carries an app- and
 * worktree-specific prefix), so every function here takes the name as an
 * argument. All helpers are no-ops outside the browser.
 */

const SUPPORTED = ['en', 'fr'] as const;
export type LanguageCode = (typeof SUPPORTED)[number];

export function isLanguageCode(value: string | null | undefined): value is LanguageCode {
  return value === 'en' || value === 'fr';
}

/** Read a cookie value by name, or `null` if absent / not in a browser. */
export function readLanguageCookie(name: string): LanguageCode | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) {
      const value = decodeURIComponent(part.slice(prefix.length));
      return isLanguageCode(value) ? value : null;
    }
  }
  return null;
}

/** Persist the language to a cookie with a one-year expiry (SameSite=Lax). */
export function writeLanguageCookie(name: string, value: LanguageCode): void {
  if (typeof document === 'undefined') return;
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}
